import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Row, Tabs } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import CancelDeliveryModal from '../../components/CancelDeliveryModal';
import Page from '../../components/Page/Page';
import Title from '../../components/Page/Title';
import { Delivery } from '../../api/models/deliveries';
import { deliveriesSelector } from '../../redux/selectors/deliveries';
import i18next from '../../i18next';
import styles from './ActiveDeliveryPageV2.module.less';
import { activeDeliveriesPageOpen, deliveryDetailsClicked, yesCancelClicked } from '../../utils/analytika/events';
import { ANALYTIKA_EVENTS_V2, ANALYTIKA_SCREENS } from '../../utils/analytika/enums';
import { routes } from '../../utils/routerHelper';
import { useHistory, useParams } from 'react-router-dom';
import { whenOrderIsCanceled, whenOrderIsFailedToCancel } from './messages';
import { getErrorFromResponse } from '../../utils/apiHelpers';
import { ACTIVE_DELIVERIES_LIMIT, ACTIVE_DELIVERY_TYPE } from '../../api/deliveries';
import { TablePaginationConfig } from 'antd/es/table/interface';
import ActiveDeliveryExpanded from '../../components/ActiveDeliveryExpanded/ActiveDeliveryExpanded';
import { Breakpoint } from 'antd/lib/_util/responsiveObserver';
import { differenceInMinutes } from 'date-fns';
import StatusTag from '../../components/StatusTag';
import { EMPTY_PAGE_USECASE } from '../pageNotFound/EmptyPage';
import {
  displayDeliveryTimeslotDate,
  displayDeliveryTimeslotTime,
  formatDateTime,
  formatInAmericanTime,
  isNumber
} from '../../utils/componentHelpers';
import { userSelector } from '../../redux/selectors/user';
import { colorBorder } from '../../styles/theme';
import { CancellationReason, DeliveryTypeParam } from '../../api/dtos/deliveries';
import publishAV2Event from '../../utils/analytika/reporterAnalytikaV2';
import DeliveriesTable from '../../components/DeliveriesTable';
import { FeatureFlag } from '../../utils/galileo/featureFlag.enum';
import { DeliveriesDispatch, UserDispatch } from '../../types/store';
import { TimeSlotWithDate } from '../../api/models/timeslot';
import { DeliveryOption } from '../../types';

const { useBreakpoint } = Grid;

const activeDeliveryColumns = [
  {
    title: i18next.t('activeDeliveriesPage.columns.deliveryId'),
    dataIndex: 'id',
    key: 'id'
    // width: '110px'
  },
  {
    title: i18next.t('activeDeliveriesPage.columns.createdAt'),
    dataIndex: 'pickUpTime',
    key: 'pickUpTime',
    // width: '100px',
    responsive: ['md'] as Breakpoint[],
    render: (_: any, { pickUpTime }: Delivery) => (pickUpTime ? formatDateTime(pickUpTime) : 'N/A')
  },
  {
    title: i18next.t('activeDeliveriesPage.columns.orderId'),
    dataIndex: 'orderId',
    key: 'orderId',
    responsive: ['sm'] as Breakpoint[],
    width: 180
  },
  {
    title: i18next.t('activeDeliveriesPage.columns.pickupAddress'),
    dataIndex: 'pickupLocation',
    key: 'pickupLocation',
    responsive: ['md'] as Breakpoint[],
    render: (_: any, { pickupLocation }: Delivery) => pickupLocation?.nickname || pickupLocation?.name
  },
  {
    title: i18next.t('activeDeliveriesPage.columns.customerName'),
    dataIndex: 'passenger',
    key: 'passenger',
    // width: 180,
    responsive: ['xl'] as Breakpoint[],
    render: (_: any, { passenger }: Delivery) => `${passenger?.firstName} ${passenger?.lastName}`
  },
  {
    title: i18next.t('activeDeliveriesPage.columns.status'),
    dataIndex: 'status',
    key: 'status',
    // eslint-disable-next-line react/display-name
    render: (_: any, { status, eta }: Delivery) => <StatusTag status={status} eta={eta} />
  }
];

const scheduledDeliveryColumns = [
  {
    title: i18next.t('activeDeliveriesPage.scheduledTab.columns.orderId'),
    dataIndex: 'id',
    key: 'id'
    // width: '20%'
  },
  {
    title: i18next.t('activeDeliveriesPage.scheduledTab.columns.pickupAddress'),
    dataIndex: 'pickupLocation',
    key: 'pickupLocation',
    responsive: ['md'] as Breakpoint[],
    render: (_: any, { pickupLocation }: Delivery) => pickupLocation?.nickname || pickupLocation?.name
  },
  {
    title: i18next.t('activeDeliveriesPage.scheduledTab.columns.customerName'),
    dataIndex: 'passenger',
    key: 'passenger',
    // width: '20%',
    responsive: ['md'] as Breakpoint[],
    render: (_: any, { passenger }: Delivery) => `${passenger?.firstName} ${passenger?.lastName}`
  },
  {
    title: i18next.t('activeDeliveriesPage.scheduledTab.columns.pickupTime'),
    dataIndex: 'expectedPickupTime',
    key: 'expectedPickupTime',
    // width: '20%',
    render: (pickupTime: string) => {
      return pickupTime ? (
        <>
          <Row>{displayDeliveryTimeslotDate(pickupTime)}</Row>
          <Row>{formatInAmericanTime(pickupTime)}</Row>
        </>
      ) : (
        ' - '
      );
    }
  },
  {
    title: i18next.t('activeDeliveriesPage.scheduledTab.columns.deliveryTime'),
    // TODO: replace assumptions
    dataIndex: 'expectedDeliveryWindow',
    key: 'expectedDeliveryWindow',
    // width: '20%',
    responsive: ['sm'] as Breakpoint[],
    render: (expectedDeliveryWindow: TimeSlotWithDate) => {
      return expectedDeliveryWindow ? (
        <>
          <Row>{displayDeliveryTimeslotDate(expectedDeliveryWindow.date)}</Row>
          <Row>{displayDeliveryTimeslotTime(expectedDeliveryWindow)}</Row>
        </>
      ) : (
        ' - '
      );
    }
  }
];

const ActiveDeliveryPageV2 = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const REFRESH_INTERVAL = 15 * 1000;
  const {
    deliveries: { fetchActiveDeliveries, cancelDelivery, setActiveDeliveriesCurrentPage, switchActiveDeliveriesMode },
    user: { publishAnalytikaEvent }
  } = useDispatch<{
    deliveries: DeliveriesDispatch;
    user: UserDispatch;
  }>();
  const [isCancellationModalVisible, setIsCancellationModalVisible] = useState(false);
  // const [selectedDelivery, setSelectedDelivery] = useState<Delivery | undefined>(undefined);
  const [isCancellationInProgress, setIsCancellationInProgress] = useState(false);

  const urlPrams = useParams<{ deliveryId?: string }>();

  const { md: isMdBreakpoint } = useBreakpoint();

  const {
    activeDeliveries,
    activeDeliveriesPagination: { currentPage },
    deliveriesTrackingDataById,
    activeDeliveriesRequestsNumber,
    activeDeliveryTypeParam,
    activeDeliveriesCount
  } = useSelector(deliveriesSelector);

  const {
    merchantConfig: {
      accessTypes: { isPortalAccessEnabled }
    },
    userGalileoVars: { [FeatureFlag.IS_SCHEDULED_ENABLED]: isScheduledDeliveriesEnabled }
  } = useSelector(userSelector);

  const selectedDeliveryId = urlPrams.deliveryId ? [Number(urlPrams.deliveryId)] : [];

  const enrichedDeliveries = useMemo(() => {
    return activeDeliveries.map(deliveryItem => {
      const trackingInfo = deliveriesTrackingDataById[deliveryItem.id];
      const eta =
        trackingInfo && isNumber(trackingInfo.eta) ? differenceInMinutes(trackingInfo.eta * 1000, 0) || 1 : undefined;
      const driverLocationDetails = trackingInfo?.driverLocationDetails;
      return {
        ...deliveryItem,
        eta,
        driverLocationDetails
      };
    });
  }, [activeDeliveries, deliveriesTrackingDataById]);

  const selectedDelivery = useMemo(() => {
    if (selectedDeliveryId.length) {
      return enrichedDeliveries.find(item => item.id === selectedDeliveryId[0]);
    } else {
      return undefined;
    }
  }, [enrichedDeliveries, selectedDeliveryId]);

  const hasNextPage = activeDeliveriesCount >= ACTIVE_DELIVERIES_LIMIT;

  const totalCountForPagination = hasNextPage
    ? currentPage * ACTIVE_DELIVERIES_LIMIT + 1
    : currentPage * ACTIVE_DELIVERIES_LIMIT;

  const postOrderCancellation = useCallback((error?: any) => {
    if (!error) {
      whenOrderIsCanceled();
    } else {
      whenOrderIsFailedToCancel(getErrorFromResponse(error));
    }
    setIsCancellationInProgress(false);
  }, []);

  const onCancelDeliveryHandler = useCallback(
    (reason: CancellationReason) => {
      if (!selectedDelivery) {
        return;
      }
      publishAnalytikaEvent(
        yesCancelClicked(ANALYTIKA_SCREENS.MARKETPLACE_ACTIVE_DELIVERIES, selectedDelivery, reason)
      );
      publishAV2Event({
        event_name: ANALYTIKA_EVENTS_V2.EXPR_CLICK_CANCEL_DELIVERY,
        event_version: 2,
        cancel_reason: reason,
        screen_name: ANALYTIKA_SCREENS.MARKETPLACE_ACTIVE_DELIVERIES,
        delivery_id: selectedDelivery.id,
        value: selectedDelivery.bookingId
      });
      setIsCancellationInProgress(true);
      setIsCancellationModalVisible(false);
      return cancelDelivery({ deliveryId: selectedDelivery.bookingId, reason })
        .then(() => {
          postOrderCancellation();
          history.push(routes.activeDeliveries);
        })
        .catch(error => {
          postOrderCancellation(error);
        });
    },
    [selectedDelivery]
  );

  useEffect(() => {
    fetchActiveDeliveries(ACTIVE_DELIVERY_TYPE.MARKETPLACE).then(deliveries => {
      publishAnalytikaEvent(activeDeliveriesPageOpen({ completed_deliveries_list: deliveries }));
    });
    const intervalId = setInterval(() => fetchActiveDeliveries(ACTIVE_DELIVERY_TYPE.MARKETPLACE), REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, []);

  const onPaginateHandler = async (conf: TablePaginationConfig) => {
    const selectedPage = conf.current || 1;
    setActiveDeliveriesCurrentPage(selectedPage);
    await fetchActiveDeliveries(ACTIVE_DELIVERY_TYPE.MARKETPLACE);

    if (currentPage < selectedPage) {
      publishAV2Event({
        event_name: ANALYTIKA_EVENTS_V2.EXPR_CLICK_BUTTON,
        event_version: 3,
        button_name: `${activeDeliveryTypeParam === DeliveryTypeParam.SCHEDULED ? DeliveryOption.SCHEDULED : DeliveryOption.INSTANT}_deliveries_next_page`,
        page: selectedPage
      });
    } else {
      publishAV2Event({
        event_name: ANALYTIKA_EVENTS_V2.EXPR_CLICK_BUTTON,
        event_version: 3,
        button_name: `${activeDeliveryTypeParam === DeliveryTypeParam.SCHEDULED ? DeliveryOption.SCHEDULED : DeliveryOption.INSTANT}_deliveries_prev_page`,
        page: selectedPage
      });
    }

    history.push(routes.activeDeliveries);
  };

  const handleExpandClickTable = useCallback((expanded: boolean, deliverySelected: Delivery) => {
    if (expanded) {
      publishAnalytikaEvent(
        deliveryDetailsClicked(ANALYTIKA_SCREENS.MARKETPLACE_ACTIVE_DELIVERIES, { delivery_details: deliverySelected })
      );
      publishAV2Event({
        event_name: ANALYTIKA_EVENTS_V2.EXPR_CLICK_ORDER_DETAILS,
        event_version: 2,
        order_details: JSON.stringify(deliverySelected)
      });
      history.push(routes.activeDeliveriesWithParam.replace(':deliveryId', `${deliverySelected.id}`));
    } else {
      history.push(routes.activeDeliveries);
    }
  }, []);

  const handleSwitchActiveTab = (key: string) => {
    switchActiveDeliveriesMode(key as DeliveryTypeParam);
    publishAV2Event({
      event_name: ANALYTIKA_EVENTS_V2.EXPR_CLICK_BUTTON,
      screen_name: ANALYTIKA_SCREENS.MARKETPLACE_ACTIVE_DELIVERIES,
      event_version: 3,
      button_name: key === DeliveryTypeParam.SCHEDULED ? 'scheduled_deliveries_list' : 'instant_deliveries_list'
    });
  };

  return (
    <Page className={styles.activeDeliveriesPage}>
      {isMdBreakpoint && <Title text={t('activeDeliveriesPage.title')} />}
      <Row style={{ border: `1px solid ${colorBorder}`, borderRadius: 8, padding: 16 }}>
        <Tabs
          defaultActiveKey={activeDeliveryTypeParam}
          className={!isScheduledDeliveriesEnabled ? styles.tabsNavContainerInvisible : ''}
          onChange={handleSwitchActiveTab}
          style={{ flex: '1 1 100%' }}
          items={[
            {
              label: t('activeDeliveriesPage.title'),
              key: DeliveryTypeParam.INSTANT,
              disabled: !activeDeliveriesRequestsNumber,
              children: (
                <DeliveriesTable
                  isEmpty={!enrichedDeliveries.length && !hasNextPage}
                  isLoading={!activeDeliveriesRequestsNumber}
                  emptyUseCase={EMPTY_PAGE_USECASE.ACTIVE_EMPTY}
                  emptyHasCta={isPortalAccessEnabled}
                  columns={activeDeliveryColumns}
                  dataSource={enrichedDeliveries}
                  onChange={onPaginateHandler}
                  pagination={{
                    defaultPageSize: ACTIVE_DELIVERIES_LIMIT,
                    total: totalCountForPagination
                  }}
                  expandable={{
                    defaultExpandAllRows: false,
                    expandRowByClick: true,
                    expandedRowKeys: selectedDeliveryId,
                    onExpand: handleExpandClickTable,
                    expandedRowRender: (delivery: Delivery, index, indent, isExpanded) =>
                      isExpanded ? (
                        <ActiveDeliveryExpanded
                          openCancellationModal={() => setIsCancellationModalVisible(true)}
                          isCancellationInProgress={isCancellationInProgress}
                          delivery={delivery}
                        />
                      ) : (
                        ''
                      )
                  }}
                />
              )
            },
            {
              label: t('activeDeliveriesPage.scheduledTab.title'),
              key: DeliveryTypeParam.SCHEDULED,
              disabled: !activeDeliveriesRequestsNumber,
              children: (
                <DeliveriesTable
                  isEmpty={!activeDeliveriesCount}
                  isLoading={!activeDeliveriesRequestsNumber}
                  emptyUseCase={EMPTY_PAGE_USECASE.ACTIVE_EMPTY}
                  emptyHasCta={isPortalAccessEnabled}
                  columns={scheduledDeliveryColumns}
                  dataSource={enrichedDeliveries}
                  onChange={onPaginateHandler}
                  pagination={{
                    defaultPageSize: ACTIVE_DELIVERIES_LIMIT,
                    total: totalCountForPagination
                  }}
                  expandable={{
                    defaultExpandAllRows: false,
                    expandRowByClick: true,
                    expandedRowKeys: selectedDeliveryId,
                    onExpand: handleExpandClickTable,
                    expandedRowRender: (delivery: Delivery, index, indent, isExpanded) =>
                      isExpanded ? (
                        <ActiveDeliveryExpanded
                          openCancellationModal={() => setIsCancellationModalVisible(true)}
                          isCancellationInProgress={isCancellationInProgress}
                          delivery={delivery}
                        />
                      ) : (
                        ''
                      )
                  }}
                />
              )
            }
          ]}
        ></Tabs>
      </Row>
      {isCancellationModalVisible && (
        <CancelDeliveryModal
          isVisible={isCancellationModalVisible}
          onClose={() => setIsCancellationModalVisible(false)}
          onConfirm={onCancelDeliveryHandler}
        />
      )}
    </Page>
  );
};

export default ActiveDeliveryPageV2;
